import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  LuSearch,
  LuMapPin,
  LuClock,
  LuUser,
  LuLayoutGrid,
  LuList,
  LuPhone,
  LuBookOpen,
  LuUsers,
  LuPlus,
  LuMinus,
  LuAward,
  LuX,
  LuExternalLink,
  LuLoader,
  LuNavigation,
  LuCheck,
  LuSend
} from 'react-icons/lu';
import { GiChurch } from 'react-icons/gi';
import { PiChurchLight, PiHandsPraying } from 'react-icons/pi';
import parishesHero from '../../assets/image copy 2.png';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../shared/firebase';

// Known district coordinates for distance calculation
const DISTRICT_COORDS = {
  calicut: { lat: 11.2588, lng: 75.7804, name: 'Calicut', color: 'bg-red-500', top: '60%', left: '38%' },
  kozhikode: { lat: 11.2588, lng: 75.7804, name: 'Calicut', color: 'bg-red-500', top: '60%', left: '38%' },
  kannur: { lat: 11.8745, lng: 75.3704, name: 'Kannur', color: 'bg-teal-500', top: '32%', left: '26%' },
  wayanad: { lat: 11.6854, lng: 76.1320, name: 'Wayanad', color: 'bg-amber-500', top: '48%', left: '46%' },
  malappuram: { lat: 11.0510, lng: 76.0711, name: 'Malappuram', color: 'bg-emerald-500', top: '76%', left: '44%' },
  palakkad: { lat: 10.7867, lng: 76.6548, name: 'Palakkad', color: 'bg-purple-500', top: '85%', left: '56%' },
  kasaragod: { lat: 12.5102, lng: 74.9852, name: 'Kasaragod', color: 'bg-blue-500', top: '18%', left: '20%' },
};

function getDistanceKm(lat1, lon1, lat2, lon2) {
  const R = 6371; // Radius of the Earth in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
    Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Helper to determine if a church matches a selected district
function matchesChurchDistrict(church, selectedDistrict) {
  if (!selectedDistrict || selectedDistrict === 'All Districts' || selectedDistrict === 'All') {
    return true;
  }
  const cleanTarget = selectedDistrict.toLowerCase().replace(/district/gi, '').trim();
  const cat = (church.category || church.district || '').toLowerCase();
  const addr = (church.address || church.location || '').toLowerCase();
  const name = (church.churchName || church.name || '').toLowerCase();
  const corpus = `${cat} ${addr} ${name}`;

  if (cleanTarget === 'calicut' || cleanTarget === 'kozhikode') {
    const kw = ['calicut', 'kozhikode', 'nadakkavu', 'chalappuram', 'chombala', 'vadakara', 'badagara', 'feroke', 'puthiyangadi', 'puthiyara', 'bilathikulam', 'annasseri', 'karuvampoyil', 'moorad', 'puduppadi', 'bank road', 'west hill', 'parappil'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'kannur') {
    const kw = ['kannur', 'cananore', 'thalassery', 'tellicherry', 'payyannur', 'anjarakandy', 'madai', 'kuthuparamba', 'nettur', 'chirakkal', 'mattannur'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'malappuram') {
    const kw = ['malappuram', 'malapuram', 'tirur', 'codacal', 'parappanangadi', 'tirunnavaya', 'ponnani', 'nilambur', 'manjeri'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'wayanad') {
    const kw = ['wayanad', 'wynad', 'meppadi', 'arapeta', 'mananthavady', 'manantoddy', 'thrissilery', 'koilery', 'rippon', 'bathery', 'sultan bathery', 'kalpetta', 'nedumkarana', 'nedumkaruna'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'palakkad') {
    const kw = ['palakkad', 'palghat', 'melparamba', 'panayur', 'polpully', 'pallippuram', 'robinson road', 'chittur', 'shornur', 'ottapalam'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'kasaragod') {
    const kw = ['kasaragod', 'kasaragode', 'kanhangad', 'bekal'];
    return kw.some(k => corpus.includes(k));
  }
  if (cleanTarget === 'goa') {
    const kw = ['goa', 'ponda', 'farmagudi', 'panaji', 'margao'];
    return kw.some(k => corpus.includes(k));
  }
  return corpus.includes(cleanTarget);
}

export default function OurChurches({ activeSection = 'churches', onSelectSection = () => {} }) {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [district, setDistrict] = useState('All Districts');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedChurch, setSelectedChurch] = useState(null);
  const [showMassTimingsModal, setShowMassTimingsModal] = useState(false);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [prayerFormSubmitted, setPrayerFormSubmitted] = useState(false);
  const [prayerFormData, setPrayerFormData] = useState({ name: '', phone: '', intention: '' });
  const [geoLocating, setGeoLocating] = useState(false);
  const [geoMessage, setGeoMessage] = useState('');
  const itemsPerPage = 12;

  const gridTopRef = useRef(null);

  const scrollToGrid = () => {
    if (gridTopRef.current) {
      gridTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSelectDistrict = (distName) => {
    setDistrict(distName);
    setCurrentPage(1);
    scrollToGrid();
  };

  // Fetch churches from Firestore
  useEffect(() => {
    let isMounted = true;
    async function fetchChurches() {
      try {
        setLoading(true);
        const churchesCol = collection(db, 'churches');
        const querySnapshot = await getDocs(churchesCol);

        const fetched = [];
        if (querySnapshot && !querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            fetched.push({
              id: doc.id,
              ...doc.data()
            });
          });

          // Sort by 'order' asc if available, otherwise by churchName
          fetched.sort((a, b) => {
            if (typeof a.order === 'number' && typeof b.order === 'number') {
              return a.order - b.order;
            }
            const nameA = a.churchName || a.name || '';
            const nameB = b.churchName || b.name || '';
            return nameA.localeCompare(nameB);
          });
        }

        if (isMounted) {
          setChurches(fetched);
        }
      } catch (err) {
        console.error("Error fetching churches from Firebase:", err);
        if (isMounted) {
          setChurches([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchChurches();
    return () => { isMounted = false; };
  }, []);

  // Standardized dynamic districts list
  const availableDistricts = useMemo(() => {
    const list = new Set(['Calicut', 'Kannur', 'Malappuram', 'Wayanad', 'Palakkad']);
    churches.forEach((c) => {
      const raw = c.category || c.district;
      if (raw && typeof raw === 'string') {
        const cleaned = raw.replace(/district/gi, '').trim();
        if (cleaned) {
          const capitalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
          list.add(capitalized);
        }
      }
    });
    return Array.from(list).sort();
  }, [churches]);

  // District statistics data for map and badges
  const districtData = useMemo(() => {
    const defaultList = ['Calicut', 'Kannur', 'Wayanad', 'Malappuram', 'Palakkad'];
    const allNames = Array.from(new Set([...defaultList, ...availableDistricts]));

    return allNames.map((name) => {
      const lower = name.toLowerCase();
      const meta = DISTRICT_COORDS[lower] || {
        color: 'bg-[#842343]',
        top: '50%',
        left: '50%'
      };

      const count = churches.filter((c) => matchesChurchDistrict(c, name)).length;

      return {
        name,
        color: meta.color,
        coords: { top: meta.top, left: meta.left },
        count
      };
    }).filter(d => d.count > 0 || defaultList.includes(d.name));
  }, [churches, availableDistricts]);

  // Handle Find Nearest via Geolocation
  const handleFindNearestLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    setGeoLocating(true);
    setGeoMessage('Detecting your location...');

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const userLat = pos.coords.latitude;
        const userLng = pos.coords.longitude;

        let closestDistrict = 'Calicut';
        let minDistance = Infinity;

        Object.entries(DISTRICT_COORDS).forEach(([key, info]) => {
          const dist = getDistanceKm(userLat, userLng, info.lat, info.lng);
          if (dist < minDistance) {
            minDistance = dist;
            closestDistrict = info.name;
          }
        });

        setGeoLocating(false);
        setGeoMessage(`Closest: ${closestDistrict} (~${Math.round(minDistance)} km away)`);
        setDistrict(closestDistrict);
        setCurrentPage(1);
        scrollToGrid();
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setGeoLocating(false);
        setGeoMessage('Location permission denied. Please select a district.');
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // Handle Prayer Request Form
  const handlePrayerSubmit = async (e) => {
    e.preventDefault();
    if (!prayerFormData.intention.trim()) return;

    setPrayerFormSubmitted(true);

    try {
      // Calculate order index
      let currentOrder = 1;
      try {
        let snap = await getDocs(collection(db, 'contact-us')).catch(() => null);
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'contact_us')).catch(() => null);
        }
        if (snap) {
          currentOrder = snap.size + 1;
        }
      } catch (err) {
        currentOrder = Date.now();
      }

      const messageDoc = {
        Name: (prayerFormData.name || 'Anonymous').trim(),
        name: (prayerFormData.name || 'Anonymous').trim(),
        Email: '',
        email: '',
        Phone: (prayerFormData.phone || '').trim(),
        phone: (prayerFormData.phone || '').trim(),
        Category: 'Prayer Request',
        category: 'Prayer Request',
        Message: prayerFormData.intention.trim(),
        message: prayerFormData.intention.trim(),
        order: currentOrder,
        time_stamp: new Date(),
        createdAt: serverTimestamp()
      };

      // Save to Firestore contact-us collection (with fallbacks)
      try {
        await addDoc(collection(db, 'contact-us'), messageDoc);
      } catch (err) {
        try {
          await addDoc(collection(db, 'contact_us'), messageDoc);
        } catch (e2) {
          await addDoc(collection(db, 'contacts'), messageDoc);
        }
      }
    } catch (err) {
      console.error("Error saving prayer request to Firestore:", err);
    }

    setTimeout(() => {
      setPrayerFormSubmitted(false);
      setShowPrayerModal(false);
      setPrayerFormData({ name: '', phone: '', intention: '' });
    }, 2500);
  };

  // Multi-token intelligent search and regional filtering
  const filtered = useMemo(() => {
    return churches.filter((c) => {
      // 1. District matching
      const matchesDistrict = matchesChurchDistrict(c, district);
      if (!matchesDistrict) return false;

      // 2. Multi-token search matching
      if (searchTerm.trim()) {
        const q = searchTerm.trim().toLowerCase();
        const name = (c.churchName || c.name || '').toLowerCase();
        const addr = (c.address || c.location || '').toLowerCase();
        const priest = (c.preistName || c.priestName || c.priest || '').toLowerCase();
        const cat = (c.category || c.district || '').toLowerCase();
        const corpus = `${name} ${addr} ${priest} ${cat}`;

        const tokens = q.split(/\s+/).filter(Boolean);
        const matchesAll = tokens.every(tok => corpus.includes(tok));
        if (!matchesAll) return false;
      }

      return true;
    });
  }, [churches, searchTerm, district]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const displayedChurches = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const totalCount = churches.length;

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#2D2420] font-sans pb-16">

      {/* ── 1. Hero Banner ── */}
      <section className="relative w-full overflow-hidden min-h-[380px] sm:min-h-[420px] lg:min-h-[440px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={parishesHero}
            alt="CSI Parishes Cathedral"
            className="w-full h-full object-cover object-center md:object-[right_center] filter brightness-[0.75] contrast-[1.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#140e1b]/90 via-[#140e1b]/40 to-black/30"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/40"></div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 sm:pt-32 pb-16 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          
          <div className="max-w-xl text-left">
            <h1 className="font-serif font-bold text-white text-[38px] sm:text-[48px] lg:text-[56px] tracking-[0.04em] uppercase leading-[1.05] drop-shadow-md">
              OUR PARISHES
            </h1>
            <p className="text-[#E8B86D] text-[15px] sm:text-[17px] font-medium tracking-wide mt-2.5 drop-shadow-sm">
              We are one family in Christ. Our parishes are the spiritual homes where faith is nurtured, worship is celebrated, and communities are built.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium mt-4">
              <span onClick={() => onSelectSection('home')} className="hover:text-white cursor-pointer transition-colors">Home</span>
              <span className="text-[#E8B86D] font-bold">›</span>
              <span className="text-white font-semibold">Parishes</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <LuAward className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">26+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Diocese</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <PiChurchLight className="w-6 h-6 text-[#E8B86D]" />
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">
                  {availableDistricts.length > 0 ? availableDistricts.length : '5'}
                </span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center gap-3 bg-[#7B1E3A]/90 hover:bg-[#7B1E3A] backdrop-blur-md text-white rounded-2xl px-4 py-3 border border-white/15 shadow-[0_8px_25px_rgba(0,0,0,0.3)] transition-all">
              <span className="text-[#E8B86D] text-xl font-serif leading-none">✝</span>
              <div>
                <span className="font-serif font-bold text-white text-lg leading-none block">4.5M+</span>
                <span className="text-[11px] uppercase tracking-wider text-white/80 font-bold">Members</span>
              </div>
            </div>
          </div>

        </div>

        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-none z-20 pointer-events-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-10 sm:h-12 text-[#FAF7F2] fill-current">
            <path d="M0,0 C150,90 350,-40 500,60 C650,160 900,10 1200,40 L1200,120 L0,120 Z"></path>
          </svg>
        </div>
      </section>

      {/* Main Content Area */}
      <div ref={gridTopRef} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-6 space-y-8">

        {/* ── 2. Search & Filter Bar ── */}
        <div className="bg-white rounded-[20px] shadow-[0_10px_35px_rgba(0,0,0,0.04)] p-5 sm:p-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3.5 items-center">

            {/* Search Input */}
            <div className="md:col-span-6 relative flex items-center">
              <input
                type="text"
                placeholder="Search parish name, address, or priest..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-[#fdfaf7] border border-gray-200 rounded-xl pl-4 pr-10 py-2.5 text-[13px] text-[#1a1715] placeholder:text-[#9e918b] outline-none focus:border-[#842343] transition-colors"
              />
              <LuSearch className="w-4 h-4 text-[#8A7E79] absolute right-3.5 pointer-events-none" />
            </div>

            {/* Districts Select */}
            <div className="md:col-span-4">
              <select
                value={district}
                onChange={(e) => handleSelectDistrict(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-[13px] text-[#5A4F4A] outline-none focus:border-[#842343] transition-colors cursor-pointer"
              >
                <option value="All Districts">All Districts ({churches.length})</option>
                {availableDistricts.map((dist) => (
                  <option key={dist} value={dist}>{dist}</option>
                ))}
              </select>
            </div>

            {/* Reset Button */}
            <div className="md:col-span-2">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDistrict('All Districts');
                  setCurrentPage(1);
                }}
                className="w-full bg-[#842343] hover:bg-[#6c1b35] text-white font-sans font-semibold text-[13px] py-2.5 px-5 rounded-xl shadow-sm transition-all duration-300 active:scale-95 text-center cursor-pointer"
              >
                Reset Filter
              </button>
            </div>

          </div>

          {/* Bottom Tags & View Switcher Row */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5 pt-4 border-t border-gray-100">
            {/* Popular Areas Tags */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold text-[#8A7E79] mr-1">Districts:</span>
              <button
                onClick={() => handleSelectDistrict('All Districts')}
                className={`text-[11px] font-medium px-3 py-1 rounded-full border transition-all cursor-pointer ${district === 'All Districts'
                  ? 'bg-[#842343] text-white border-[#842343]'
                  : 'bg-[#faf6f2] text-[#5A4F4A] border-gray-200 hover:bg-white hover:border-[#842343]'
                  }`}
              >
                All
              </button>
              {availableDistricts.map((pArea) => (
                <button
                  key={pArea}
                  onClick={() => handleSelectDistrict(pArea)}
                  className={`text-[11px] font-medium px-3 py-1 rounded-full border transition-all cursor-pointer ${district === pArea
                    ? 'bg-[#842343] text-white border-[#842343]'
                    : 'bg-[#faf6f2] text-[#5A4F4A] border-gray-200 hover:bg-white hover:border-[#842343]'
                    }`}
                >
                  {pArea}
                </button>
              ))}
            </div>

            {/* View Mode Switcher */}
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${viewMode === 'grid'
                  ? 'bg-[#842343] text-white shadow-sm'
                  : 'bg-white text-[#5A4F4A] border border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <LuLayoutGrid className="w-3.5 h-3.5" />
                <span>Grid View</span>
              </button>

              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${viewMode === 'list'
                  ? 'bg-[#842343] text-white shadow-sm'
                  : 'bg-white text-[#5A4F4A] border border-gray-200 hover:bg-gray-50'
                  }`}
              >
                <LuList className="w-3.5 h-3.5" />
                <span>List View</span>
              </button>
            </div>
          </div>
        </div>

        {/* ── 3. Parishes List / Grid (PERFECTLY ALIGNED) ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <LuLoader className="w-10 h-10 text-[#842343] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Connecting to Firebase & loading parishes...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[24px] border border-gray-100 shadow-sm">
            <GiChurch className="w-12 h-12 text-[#8A7E79]/50 mx-auto mb-3" />
            <h3 className="font-serif font-bold text-lg text-[#1a1715]">No Parishes Found</h3>
            <p className="text-xs text-[#8A7E79] mt-1">Try adjusting your search criteria or district filter.</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setDistrict('All Districts');
              }}
              className="mt-4 px-4 py-2 bg-[#842343] text-white text-xs font-semibold rounded-lg hover:bg-[#6c1b35] transition-colors cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {displayedChurches.map((church) => {
              const churchTitle = church.churchName || church.name || 'CSI Church';
              const churchAddress = church.address || church.location || 'Diocese of Malabar';
              const priest = church.preistName || church.priestName || church.priest;
              const imgUrl = church.churchImg || church.img || parishesHero;
              const mapUrl = church.map_link && church.map_link.trim() !== ''
                ? church.map_link
                : `https://maps.google.com/?q=${encodeURIComponent(churchTitle + ' ' + churchAddress)}`;

              return (
                <div
                  key={church.id}
                  className="bg-white rounded-[22px] overflow-hidden border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_30px_rgba(132,35,67,0.09)] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1 h-full"
                >
                  <div>
                    {/* Image */}
                    <div className="relative h-44 w-full overflow-hidden bg-gray-100">
                      <img
                        src={imgUrl}
                        alt={churchTitle}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = parishesHero;
                        }}
                        className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
                      />
                    </div>

                    {/* Card Body with Compact Height */}
                    <div className="p-3 sm:p-3.5 pb-1 flex flex-col">
                      <h3
                        className="font-serif font-bold text-[#842343] text-sm sm:text-[15px] leading-tight mb-1 group-hover:text-[#6c1b35] transition-colors line-clamp-2 min-h-[36px] flex items-center"
                        title={churchTitle}
                      >
                        {churchTitle}
                      </h3>

                      <p
                        className="text-[11px] text-[#8A7E79] font-medium leading-snug mb-1.5 line-clamp-2 min-h-[30px]"
                        title={churchAddress}
                      >
                        {churchAddress}
                      </p>

                      <div className="space-y-1 text-[11px] text-[#5A4F4A] mb-1 min-h-[32px]">
                        {priest && priest.trim() !== '' && (
                          <div className="flex items-center gap-1.5">
                            <LuUser className="w-3 h-3 text-[#842343] shrink-0" />
                            <span className="truncate">Vicar: <strong className="text-[#1a1715] font-semibold">{priest}</strong></span>
                          </div>
                        )}
                        {church.phoneno && church.phoneno !== '0' && church.phoneno.trim() !== '' && (
                          <div className="flex items-center gap-1.5">
                            <LuPhone className="w-3 h-3 text-[#842343] shrink-0" />
                            <span>Phone: <strong className="text-[#1a1715] font-semibold">{church.phoneno}</strong></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Aligned 2-Button Bottom Row */}
                  <div className="p-3 sm:p-3.5 pt-0">
                    <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-gray-100">
                      <button
                        onClick={() => setSelectedChurch(church)}
                        className="bg-[#842343] hover:bg-[#6c1b35] text-white text-[11px] font-semibold py-1.5 px-2 rounded-lg text-center shadow-xs transition-colors cursor-pointer"
                      >
                        View Details
                      </button>
                      <a
                        href={mapUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-transparent hover:bg-[#faf6f2] text-[#8A7E79] hover:text-[#842343] border border-[#e5c98b]/80 text-[11px] font-semibold py-1.5 px-2 rounded-lg text-center transition-colors flex items-center justify-center gap-1"
                      >
                        <span>Directions</span>
                        <LuExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {displayedChurches.map((church) => {
              const churchTitle = church.churchName || church.name || 'CSI Church';
              const churchAddress = church.address || church.location || 'Diocese of Malabar';
              const priest = church.preistName || church.priestName || church.priest;
              const imgUrl = church.churchImg || church.img || parishesHero;
              const mapUrl = church.map_link && church.map_link.trim() !== ''
                ? church.map_link
                : `https://maps.google.com/?q=${encodeURIComponent(churchTitle + ' ' + churchAddress)}`;

              return (
                <div
                  key={church.id}
                  className="bg-white rounded-[18px] p-4 border border-gray-100 shadow-xs hover:shadow-md transition-all flex flex-col md:flex-row items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4 w-full md:w-auto">
                    <img
                      src={imgUrl}
                      alt={churchTitle}
                      className="w-16 h-16 rounded-xl object-cover shrink-0"
                    />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-serif font-bold text-[#842343] text-base">{churchTitle}</h4>
                        {church.category && (
                          <span className="bg-[#842343]/10 text-[#842343] text-[10px] font-bold px-2 py-0.5 rounded-full">
                            {church.category}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#8A7E79] mt-0.5 flex items-center gap-1">
                        <LuMapPin className="w-3 h-3 text-[#842343]" /> {churchAddress}
                      </p>
                      {priest && (
                        <p className="text-xs text-[#5A4F4A] mt-1 flex items-center gap-1">
                          <LuUser className="w-3 h-3 text-[#842343]" /> Priest: {priest}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                    <button
                      onClick={() => setSelectedChurch(church)}
                      className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-semibold py-2 px-4 rounded-lg shadow-xs transition-colors cursor-pointer"
                    >
                      View Details
                    </button>
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white hover:bg-gray-50 text-[#842343] border border-gray-200 text-xs font-semibold py-2 px-3 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <span>Directions</span>
                      <LuExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── 4. Pagination ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4 pb-4">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(p => Math.max(1, p - 1));
                scrollToGrid();
              }}
              className={`w-8 h-8 rounded-lg bg-white border border-gray-200 text-xs font-semibold ${currentPage === 1 ? 'opacity-40 cursor-not-allowed text-gray-400' : 'text-[#8A7E79] hover:border-[#842343] hover:text-[#842343]'
                } transition-colors flex items-center justify-center cursor-pointer`}
            >
              «
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => {
                  setCurrentPage(num);
                  scrollToGrid();
                }}
                className={`w-8 h-8 rounded-full text-xs font-bold transition-all cursor-pointer ${currentPage === num
                  ? 'bg-[#842343] text-white shadow-xs'
                  : 'bg-white border border-gray-200 text-[#5A4F4A] hover:border-[#842343]'
                  }`}
              >
                {num}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => {
                setCurrentPage(p => Math.min(totalPages, p + 1));
                scrollToGrid();
              }}
              className={`w-8 h-8 rounded-lg bg-white border border-gray-200 text-xs font-semibold ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed text-gray-400' : 'text-[#8A7E79] hover:border-[#842343] hover:text-[#842343]'
                } transition-colors flex items-center justify-center cursor-pointer`}
            >
              »
            </button>
          </div>
        )}

        {/* ── 5. Bottom Interactive & Informational Row (FIND PARISHES NEAR YOU) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* Left: Find Parishes Near You + Map (Col 7) */}
          <div className="lg:col-span-7 bg-white rounded-[24px] p-6 shadow-[0_6px_25px_rgba(0,0,0,0.03)] border border-gray-100 flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-5/12 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-serif font-bold text-[18px] text-[#1a1715]">
                    Find Parishes Near You
                  </h3>
                </div>
                <p className="text-[11px] text-[#8A7E79] mb-3">
                  Click a district to filter parishes instantly
                </p>

                {/* GPS / Geolocation Button */}
                <button
                  onClick={handleFindNearestLocation}
                  disabled={geoLocating}
                  className="w-full mb-3.5 bg-[#FAF6F2] hover:bg-[#842343] text-[#842343] hover:text-white border border-[#E8DDD7] px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer"
                >
                  <LuNavigation className={`w-3.5 h-3.5 ${geoLocating ? 'animate-spin' : ''}`} />
                  <span>{geoLocating ? 'Locating...' : 'Use My GPS Location'}</span>
                </button>

                {geoMessage && (
                  <p className="text-[11px] text-[#842343] font-semibold mb-2 bg-[#FDF3F0] p-1.5 rounded-lg border border-[#F0D1C8]">
                    {geoMessage}
                  </p>
                )}

                {/* District List with Active Highlight */}
                <div className="space-y-1.5">
                  {districtData.map((d, i) => {
                    const isSelected = district.toLowerCase() === d.name.toLowerCase();

                    return (
                      <div
                        key={i}
                        onClick={() => handleSelectDistrict(d.name)}
                        className={`flex items-center justify-between p-2 rounded-xl transition-all cursor-pointer group ${isSelected
                          ? 'bg-[#842343] text-white shadow-xs'
                          : 'hover:bg-[#faf6f2] text-[#2D2420]'
                          }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className={`w-6 h-6 rounded-full ${d.color} text-white flex items-center justify-center text-[10px] font-bold shadow-xs`}>
                            <LuMapPin className="w-3.5 h-3.5" />
                          </div>
                          <span className={`font-sans text-[13px] font-semibold ${isSelected ? 'text-white' : 'text-[#2D2420] group-hover:text-[#842343]'} transition-colors`}>
                            {d.name}
                          </span>
                        </div>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-[#8A7E79]'}`}>
                          {d.count}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={() => handleSelectDistrict('All Districts')}
                className="mt-4 w-full bg-[#842343] hover:bg-[#6c1b35] text-white text-[12px] font-semibold py-2.5 rounded-xl shadow-xs transition-colors cursor-pointer"
              >
                View All Parishes ({totalCount})
              </button>
            </div>

            {/* Right Map Canvas Simulation */}
            <div className="w-full md:w-7/12 h-[260px] md:h-auto min-h-[260px] bg-[#e4edd9] rounded-2xl relative overflow-hidden border border-gray-200">
              <div className="absolute inset-0 opacity-60 bg-[radial-gradient(#c5d8b8_2px,transparent_2px)] [background-size:16px_16px]"></div>

              <svg className="absolute inset-0 w-full h-full text-[#c1d9b3] stroke-current" fill="none" strokeWidth="2">
                <path d="M40 0 Q60 80, 50 160 T80 260" strokeDasharray="3 3" />
                <path d="M120 0 Q140 100, 130 180 T150 280" stroke="#b0cca1" strokeWidth="1.5" />
              </svg>

              {districtData.map((d, i) => {
                const isSelected = district.toLowerCase() === d.name.toLowerCase();

                return (
                  <div
                    key={i}
                    style={d.coords}
                    onClick={() => handleSelectDistrict(d.name)}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                  >
                    <div className={`w-7 h-7 rounded-full ${d.color} text-white font-extrabold text-[10px] flex items-center justify-center shadow-md ${isSelected ? 'ring-4 ring-[#842343] scale-125' : 'ring-4 ring-white/80'} group-hover:scale-125 transition-transform`}>
                      {d.count}
                    </div>
                    <span className="absolute top-8 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-xs text-[9px] font-bold text-[#1a1715] px-1.5 py-0.5 rounded shadow-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {d.name} ({d.count})
                    </span>
                  </div>
                );
              })}

              <div className="absolute right-3 bottom-3 flex flex-col bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <button
                  onClick={() => handleSelectDistrict('All Districts')}
                  className="w-7 h-7 flex items-center justify-center hover:bg-gray-50 text-[#1a1715] border-b border-gray-100 text-sm cursor-pointer"
                  title="View All"
                >
                  <LuPlus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleSelectDistrict('Calicut')}
                  className="w-7 h-7 flex items-center justify-center hover:bg-gray-50 text-[#1a1715] text-sm cursor-pointer"
                  title="Reset to Headquarters"
                >
                  <LuMinus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

          </div>

          {/* Right Column Stack (Col 5) */}
          <div className="lg:col-span-5 flex flex-col justify-between gap-4">
            {/* Need Prayer Card */}
            <div className="bg-[#faf5ef] rounded-[22px] p-5 sm:p-6 border border-[#f2e7db] shadow-[0_4px_20px_rgba(0,0,0,0.02)] flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#842343]/10 flex items-center justify-center shrink-0">
                  <PiHandsPraying className="w-6 h-6 text-[#842343]" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-[16px] text-[#1a1715] mb-0.5">
                    Need Prayer?
                  </h4>
                  <p className="text-[12px] text-[#8A7E79] leading-snug">
                    We are here to pray for you and with you.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowPrayerModal(true)}
                className="w-full sm:w-auto bg-[#842343] hover:bg-[#6c1b35] text-white text-[12px] font-semibold py-2.5 px-5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Prayer Request
              </button>
            </div>

            {/* Direct Contact Diocesan Office Banner */}
            <div className="bg-[#842343] rounded-[22px] p-5 text-white relative overflow-hidden shadow-[0_6px_20px_rgba(132,35,67,0.2)] flex items-center justify-between">
              <div className="relative z-10 max-w-[70%]">
                <h4 className="font-serif font-bold text-[16px] leading-tight mb-1">
                  Can't find your parish?
                </h4>
                <p className="text-[11px] text-white/85 mb-3">
                  Contact the diocesan office directly for guidance.
                </p>
                <a
                  href="tel:04952721748"
                  className="inline-flex items-center gap-1.5 bg-[#e5c98b] hover:bg-[#d8b874] text-[#1a1715] text-[11px] font-bold px-4 py-2 rounded-lg shadow-sm transition-colors"
                >
                  <LuPhone className="w-3.5 h-3.5" />
                  <span>Call Diocesan Office</span>
                </a>
              </div>

              <div className="absolute right-0 bottom-0 top-0 w-1/3 opacity-30 flex items-end justify-end pointer-events-none p-2">
                <GiChurch className="w-24 h-24 text-white" />
              </div>
            </div>

          </div>

        </div>

      </div>

      {/* ── 6. Church Details Modal (WIDE FORMAT) ── */}
      {selectedChurch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-4xl lg:max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 flex flex-col">

            {/* Modal Header / Hero Image */}
            <div className="relative h-64 sm:h-72 w-full bg-gray-100 shrink-0">
              <img
                src={selectedChurch.churchImg || selectedChurch.img || 'https://images.unsplash.com/photo-1548625149-fc4a29cf7092?auto=format&fit=crop&q=80&w=600'}
                alt={selectedChurch.churchName || selectedChurch.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1548625149-fc4a29cf7092?auto=format&fit=crop&q=80&w=600';
                }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>

              <button
                onClick={() => setSelectedChurch(null)}
                className="absolute top-4 right-4 w-9 h-9 bg-white/80 hover:bg-white text-gray-800 rounded-full flex items-center justify-center shadow-lg transition-colors cursor-pointer"
              >
                <LuX className="w-5 h-5" />
              </button>

              <div className="absolute bottom-4 left-6 right-6 text-white">
                {selectedChurch.category && (
                  <span className="bg-[#842343] text-white text-[11px] font-bold px-2.5 py-0.5 rounded-full mb-2 inline-block shadow-sm">
                    {selectedChurch.category} District
                  </span>
                )}
                <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold leading-tight drop-shadow-sm">
                  {selectedChurch.churchName || selectedChurch.name}
                </h2>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 space-y-6">

              {/* Quick Info Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 bg-[#FAF7F2] p-4 rounded-2xl border border-[#E8DDD7]">

                {/* Location / Address */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#842343]/10 text-[#842343] flex items-center justify-center shrink-0 mt-0.5">
                    <LuMapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-[#8A7E79] font-bold uppercase tracking-wider block">Address</span>
                    <p className="text-xs font-semibold text-[#1a1715] leading-relaxed">
                      {selectedChurch.address || selectedChurch.location || 'Diocese of Malabar'}
                    </p>
                  </div>
                </div>

                {/* Priest Info */}
                {(selectedChurch.preistName || selectedChurch.priestName || selectedChurch.priest) && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#842343]/10 text-[#842343] flex items-center justify-center shrink-0 mt-0.5">
                      <LuUser className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[11px] text-[#8A7E79] font-bold uppercase tracking-wider block">Parish Priest</span>
                      <p className="text-xs font-semibold text-[#1a1715]">
                        {selectedChurch.preistName || selectedChurch.priestName || selectedChurch.priest}
                      </p>
                    </div>
                  </div>
                )}

                {/* Phone Number */}
                {selectedChurch.phoneno && selectedChurch.phoneno !== '0' && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#842343]/10 text-[#842343] flex items-center justify-center shrink-0 mt-0.5">
                      <LuPhone className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="text-[11px] text-[#8A7E79] font-bold uppercase tracking-wider block">Contact Phone</span>
                      <a href={`tel:${selectedChurch.phoneno}`} className="text-xs font-semibold text-[#842343] hover:underline">
                        {selectedChurch.phoneno}
                      </a>
                    </div>
                  </div>
                )}

                {/* District */}
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#842343]/10 text-[#842343] flex items-center justify-center shrink-0 mt-0.5">
                    <PiChurchLight className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-[#8A7E79] font-bold uppercase tracking-wider block">District / Deanery</span>
                    <p className="text-xs font-semibold text-[#1a1715]">
                      {selectedChurch.category || selectedChurch.district || 'Malabar'}
                    </p>
                  </div>
                </div>

              </div>

              {/* History / Description */}
              {selectedChurch.history && selectedChurch.history.trim() !== '' && (
                <div>
                  <h4 className="font-serif font-bold text-base text-[#1a1715] mb-2 flex items-center gap-2">
                    <LuBookOpen className="w-4 h-4 text-[#842343]" />
                    <span>Church History &amp; Background</span>
                  </h4>
                  <div className="text-xs sm:text-sm text-[#4A3F39] leading-relaxed bg-[#FAF8F6] p-5 rounded-2xl border border-gray-100 whitespace-pre-line font-sans">
                    {selectedChurch.history}
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setSelectedChurch(null)}
                  className="w-full sm:w-auto px-5 py-2.5 border border-gray-200 text-xs font-semibold text-[#5A4F4A] rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Close
                </button>
                <a
                  href={
                    selectedChurch.map_link && selectedChurch.map_link.trim() !== ''
                      ? selectedChurch.map_link
                      : `https://maps.google.com/?q=${encodeURIComponent((selectedChurch.churchName || selectedChurch.name) + ' ' + (selectedChurch.address || selectedChurch.location || ''))}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="w-full sm:w-auto px-6 py-2.5 bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-semibold rounded-xl shadow-xs transition-colors flex items-center justify-center gap-1.5"
                >
                  <span>Open in Google Maps</span>
                  <LuExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

            </div>

          </div>
        </div>
      )}

      {/* ── 7. Worship / Mass Timings Modal ── */}
      {showMassTimingsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-xl w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowMassTimingsModal(false)}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <LuX className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#842343] mb-1">
              <LuClock className="w-4 h-4" />
              <span>Diocesan Worship Schedule</span>
            </div>

            <h3 className="font-serif font-bold text-2xl text-[#1a1715] mb-4">
              Regular Mass &amp; Service Timings
            </h3>

            <div className="space-y-3 mb-6 text-xs sm:text-sm">
              <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-between">
                <div>
                  <strong className="text-sm font-serif text-[#1a1715] block">Sunday Malayalam Service</strong>
                  <span className="text-xs text-[#8A7E79]">Holy Eucharist &amp; Sermon</span>
                </div>
                <span className="bg-[#842343] text-white font-bold px-3 py-1.5 rounded-xl text-xs">
                  07:30 AM - 10:30 AM
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-between">
                <div>
                  <strong className="text-sm font-serif text-[#1a1715] block">Sunday English Service</strong>
                  <span className="text-xs text-[#8A7E79]">Cathedral &amp; Select Parishes</span>
                </div>
                <span className="bg-[#842343] text-white font-bold px-3 py-1.5 rounded-xl text-xs">
                  07:00 AM - 09:30 AM
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-between">
                <div>
                  <strong className="text-sm font-serif text-[#1a1715] block">Sunday Tamil Service</strong>
                  <span className="text-xs text-[#8A7E79]">Christ Tamil Church, Calicut</span>
                </div>
                <span className="bg-[#842343] text-white font-bold px-3 py-1.5 rounded-xl text-xs">
                  08:30 AM - 11:30 AM
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-between">
                <div>
                  <strong className="text-sm font-serif text-[#1a1715] block">Evening Prayer &amp; Vesper</strong>
                  <span className="text-xs text-[#8A7E79]">All Parishes</span>
                </div>
                <span className="bg-[#842343] text-white font-bold px-3 py-1.5 rounded-xl text-xs">
                  06:00 PM - 07:30 PM
                </span>
              </div>

              <div className="p-4 rounded-2xl bg-[#FAF7F2] border border-[#E8DDD7] flex items-center justify-between">
                <div>
                  <strong className="text-sm font-serif text-[#1a1715] block">Wednesday Intercession</strong>
                  <span className="text-xs text-[#8A7E79]">Healing &amp; Fasting Prayer</span>
                </div>
                <span className="bg-[#D4A64A] text-[#1a1715] font-bold px-3 py-1.5 rounded-xl text-xs">
                  10:00 AM - 01:00 PM
                </span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 flex justify-end">
              <button
                onClick={() => setShowMassTimingsModal(false)}
                className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-semibold py-2.5 px-6 rounded-xl cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── 8. Prayer Request Modal ── */}
      {showPrayerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-gray-100 relative">
            <button
              onClick={() => { setShowPrayerModal(false); setPrayerFormSubmitted(false); }}
              className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700 cursor-pointer"
            >
              <LuX className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#842343] mb-1">
              <PiHandsPraying className="w-4 h-4" />
              <span>Pastoral Intercession</span>
            </div>

            <h3 className="font-serif font-bold text-2xl text-[#1a1715] mb-2">
              Submit a Prayer Request
            </h3>
            <p className="text-xs text-[#8A7E79] mb-5">
              Our diocesan clergy and intercessory prayer fellowship will uphold your prayer intention before God.
            </p>

            {prayerFormSubmitted ? (
              <div className="bg-[#E9F9EE] border border-[#C5F0D2] p-6 rounded-2xl text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-[#25D366]/20 text-[#25D366] flex items-center justify-center mx-auto">
                  <LuCheck className="w-6 h-6" />
                </div>
                <h4 className="font-serif font-bold text-[#1a1715] text-lg">Prayer Received</h4>
                <p className="text-xs text-[#5A4F4A]">
                  Thank you. Your prayer intention has been registered with our pastoral intercession team. God bless you.
                </p>
              </div>
            ) : (
              <form onSubmit={handlePrayerSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Your Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter your name"
                    value={prayerFormData.name}
                    onChange={(e) => setPrayerFormData({ ...prayerFormData, name: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl px-3.5 py-2.5 text-xs text-[#1a1715] outline-none focus:border-[#842343]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    placeholder="Enter contact number"
                    value={prayerFormData.phone}
                    onChange={(e) => setPrayerFormData({ ...prayerFormData, phone: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl px-3.5 py-2.5 text-xs text-[#1a1715] outline-none focus:border-[#842343]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#5A4F4A] mb-1">Prayer Intention / Request *</label>
                  <textarea
                    rows={4}
                    required
                    placeholder="Describe your prayer intention..."
                    value={prayerFormData.intention}
                    onChange={(e) => setPrayerFormData({ ...prayerFormData, intention: e.target.value })}
                    className="w-full bg-[#FAF7F2] border border-[#E8DDD7] rounded-xl p-3.5 text-xs text-[#1a1715] outline-none focus:border-[#842343] resize-none"
                  ></textarea>
                </div>

                <div className="pt-2 flex items-center justify-between gap-3">
                  <a
                    href="tel:04952721748"
                    className="text-xs text-[#842343] font-semibold hover:underline flex items-center gap-1"
                  >
                    <LuPhone className="w-3 h-3" />
                    <span>Call Prayer Line: 0495 2721748</span>
                  </a>

                  <button
                    type="submit"
                    className="bg-[#842343] hover:bg-[#6c1b35] text-white text-xs font-bold py-2.5 px-6 rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                  >
                    <span>Submit</span>
                    <LuSend className="w-3.5 h-3.5" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── 9. Bottom Statistics Strip ── */}
      <section className="mt-14 pt-8 pb-8 bg-[#f5ede5]/70 border-t border-[#ede2d6]">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 text-center">

            <div className="flex items-center justify-center gap-3">
              <PiChurchLight className="w-7 h-7 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">
                  26+
                </span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Dioceses</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuBookOpen className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">
                  {availableDistricts.length > 0 ? availableDistricts.length : '5'}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Districts</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUser className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">49</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Presbyters</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">27</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Workers</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <LuUsers className="w-6 h-6 text-[#842343]" />
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">4.5M+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Members</span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-3">
              <span className="text-[#842343] text-xl font-serif">✝</span>
              <div className="text-left">
                <span className="font-serif font-bold text-lg text-[#1a1715] leading-none block">100+</span>
                <span className="text-[10px] uppercase tracking-wider text-[#8A7E79] font-bold">Worship Services</span>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
